import { QueryClient } from '@tanstack/react-query';
import { completeOnboarding } from '@services/activity-tracker/activityTrackerService';
import { ActivityDetail } from '@th-types/daily-activity.type';
import { JobAssignment } from '@th-types/job.detail.type';
import { IJobShiftsCounters, IJobShiftsResult } from '@th-types/jobs.type';
import { markShiftsPaid, markShiftsUnPaid } from '@company/services/job/api';

const getNewCountOnboarding = (
  currentCounter: number | undefined,
  totalActivities: number,
  complete: boolean
) => {
  if (typeof currentCounter === 'number') {
    if (complete) {
      return currentCounter - totalActivities;
    }
    return currentCounter + totalActivities;
  }
  return 0;
};

export async function completeJobAssignmentOnboardingUtil({
  jobAssignment,
  complete,
  shiftsList,
  queryClient,
  queryJobShiftsList,
  queryJobShiftsCounters,
}: {
  jobAssignment: JobAssignment;
  complete: boolean;
  shiftsList: ActivityDetail[] | undefined;
  queryClient: QueryClient;
  queryJobShiftsList: string[];
  queryJobShiftsCounters: string[];
}) {
  const activities = shiftsList?.filter(
    (item) => item.shiftPosition.jobAssignment?.id === jobAssignment.id
  );

  if (activities) {
    completeOnboarding([jobAssignment], activities, complete);
    // Update onboarding counter in quick filters
    queryClient.setQueryData(
      queryJobShiftsCounters,
      (existingCounters: IJobShiftsCounters | undefined) => {
        return {
          ...existingCounters,
          countOnboarding: getNewCountOnboarding(
            existingCounters?.countOnboarding,
            activities?.length || 0,
            complete
          ),
        };
      }
    );

    // Update React Query cache
    queryClient.setQueryData(
      queryJobShiftsList,
      (existingData: IJobShiftsResult | undefined) => {
        if (!existingData) return existingData;
        return {
          ...existingData,
          jobShifts: existingData.jobShifts.map((jobShiftsData) => {
            if (
              jobShiftsData.jobShiftActivities.some(
                (act) =>
                  act.shiftPosition.jobAssignment?.id === jobAssignment.id
              )
            ) {
              return {
                ...jobShiftsData,
                jobShiftActivities: jobShiftsData.jobShiftActivities.map(
                  (activityItem) => {
                    const itemAssignment =
                      activityItem.shiftPosition.jobAssignment;
                    if (itemAssignment?.id === jobAssignment.id) {
                      return {
                        ...activityItem,
                        shiftPosition: {
                          ...activityItem.shiftPosition,
                          jobAssignment: {
                            ...itemAssignment,
                            onBoardingCompleted: complete,
                          },
                        },
                      };
                    }
                    return activityItem;
                  }
                ),
              };
            }
            return jobShiftsData;
          }),
        };
      }
    );
  }
}

const getNewCountMarkAsPaid = (
  countMarkPaid: number | undefined,
  isShiftPaid: boolean
) => {
  if (typeof countMarkPaid === 'number') {
    if (!isShiftPaid) {
      return countMarkPaid - 1;
    }
    return countMarkPaid + 1;
  }
  return 0;
};

export async function markShiftAsPaid(
  activity: ActivityDetail,
  queryClient: QueryClient,
  queryJobShiftsList: string[],
  queryJobShiftsCounters: string[]
) {
  const isShiftPaid = activity.shiftPosition.isPaid;

  const result = !isShiftPaid
    ? await markShiftsPaid(activity.shiftPosition.jobId, [
        activity.shiftPosition.id,
      ])
    : await markShiftsUnPaid(activity.shiftPosition.jobId, [
        activity.shiftPosition.id,
      ]);

  const resultMessage = result.message;
  if (result.success) {
    // Update countMarkPaid counter in quick filters
    queryClient.setQueryData(
      queryJobShiftsCounters,
      (existingCounters: IJobShiftsCounters | undefined) => {
        return {
          ...existingCounters,
          countMarkPaid: getNewCountMarkAsPaid(
            existingCounters?.countMarkPaid,
            isShiftPaid
          ),
        };
      }
    );
    queryClient.setQueryData(
      queryJobShiftsList,
      (existingData: IJobShiftsResult | undefined) => {
        if (!existingData) return existingData;
        return {
          ...existingData,
          jobShifts: existingData.jobShifts.map((jobShiftsData) => {
            if (
              jobShiftsData.jobShiftActivities.some(
                (act) => act.shiftPosition.id === activity.shiftPosition.id
              )
            ) {
              const updatedJobShiftActivities =
                jobShiftsData.jobShiftActivities.map((activityItem) => {
                  if (
                    activityItem.shiftPosition.id === activity.shiftPosition.id
                  ) {
                    return {
                      ...activityItem,
                      shiftPosition: {
                        ...activityItem.shiftPosition,
                        isPaid: !activity.shiftPosition.isPaid,
                      },
                    };
                  }
                  return activityItem;
                });
              return {
                ...jobShiftsData,
                jobShiftActivities: updatedJobShiftActivities,
              };
            }
            return jobShiftsData;
          }),
        };
      }
    );
  }
  return resultMessage;
}
